import { cn } from '@/lib/cn'
import { Badge } from '@/components/ui'
import { useI18n } from '@/i18n'
import type { CatalogModel } from '@/types'
import { ModalityIcon } from './ModalityIcon'
import { billingModeKey } from './BillingLabel'

const ROUTING_TIER_VARIANT: Record<string, 'default' | 'secondary' | 'success' | 'warning' | 'info' | 'outline'> = {
  official: 'success',
  stable: 'info',
  dev: 'warning',
  bltcy: 'secondary',
}

const KNOWN_TIERS = new Set(['official', 'stable', 'dev', 'bltcy'])

export interface ModelBadgeProps {
  model: CatalogModel
  className?: string
}

/**
 * Compact one-row chip strip for a catalog model:
 * modality icon + routing tier + quality tier + billing mode.
 * Reads catalog-native descriptors directly; labels are i18n-keyed.
 */
export function ModelBadge({ model, className }: ModelBadgeProps) {
  const { t } = useI18n()
  const tier = model.routing_tier ?? undefined
  const tierVariant = tier ? (ROUTING_TIER_VARIANT[tier] ?? 'outline') : 'outline'
  const tierLabel = tier ? (KNOWN_TIERS.has(tier) ? t(`modelSelect.tier.${tier}`) : tier) : ''

  return (
    <span className={cn('inline-flex items-center gap-1.5 flex-wrap', className)}>
      <ModalityIcon modality={model.capability.modality} withLabel />
      {tier && <Badge variant={tierVariant}>{tierLabel}</Badge>}
      {model.quality_tier_display && (
        <Badge variant="outline">
          {model.quality_tier_icon ? `${model.quality_tier_icon} ` : ''}
          {model.quality_tier_display}
        </Badge>
      )}
      <Badge variant="secondary">{t(`modelSelect.billingMode.${billingModeKey(model.billing)}`)}</Badge>
    </span>
  )
}
